import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { createTask, updateTask } from '../../../api/task'
import { fetchStudentApplications } from '../../../api/applications'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { ReactComponent as StudentsIcon } from 'assets/icons/people.svg'
import { ReactComponent as TypeIcon } from 'assets/icons/dock.svg'
import { ReactComponent as TimeIcon } from 'assets/icons/clock.svg'
import { ReactComponent as DescrIcon } from 'assets/icons/message.svg'
import { ReactComponent as ReviewIcon } from 'assets/icons/openedEye.svg'
import {
	MOCK_ACTIVITIES,
	DEFAULT_DESCRIPTIONS_BY_TYPE,
	ACTIVITY_TYPES,
} from '../constants'
import { useIsMobile } from 'shared/hooks/useBreakpoint'
import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'

const parseEssayWordLimit = value => {
	const raw = String(value || '').trim()
	if (!raw) return null
	const asNumber = Number(raw)
	if (Number.isFinite(asNumber) && asNumber > 0) {
		return Math.floor(asNumber)
	}
	const digitsOnly = raw.replace(/[^\d]/g, '')
	if (!digitsOnly) return null
	const parsed = Number.parseInt(digitsOnly, 10)
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const isEssayAutoDescription = text =>
	/^Please write an essay ".*"(?: for (?:Common App|the selected university))?, with a maximum word count of \d+ words\.$/i.test(
		String(text || '').trim(),
	)

export const useAssignActivityModal = ({
	isOpen,
	onClose,
	selectedStudents = [],
	savedLists = [],
	activeGrade = 'all',
	onSelectList,
	onSetGrade,
	onSuccess,
	clearSelectionOnOpen = true,
	editMode = false,
	assignment = null,
	tepeModal = '',
	initialAssignmentType = '',
	initialUniversityId = '',
	initialUniversityName = '',
	initialUniversityLogoUrl = '',
	initialApplicationTargetId = '',
	initialEssayMode = '',
	initialTaskTitle = '',
	initialStepId = '',
	lockRecipients = false,
}) => {
	const { showErrorSafe } = useSafeErrorBanner()
	const applicationUniversityId = String(initialUniversityId || '').trim()
	const applicationUniversityName = String(initialUniversityName || '').trim()
	const applicationUniversityLogoUrl = String(
		initialUniversityLogoUrl || '',
	).trim()
	const applicationTargetId = String(initialApplicationTargetId || '').trim()
	const [essayUniversityOptions, setEssayUniversityOptions] = useState([])
	const [isEssayUniversityLoading, setIsEssayUniversityLoading] = useState(false)
	const [selectedEssayUniversityTargetId, setSelectedEssayUniversityTargetId] =
		useState(applicationTargetId)
	const [selectedEssayUniversityMeta, setSelectedEssayUniversityMeta] = useState({
		universityId: applicationUniversityId,
		universityName: applicationUniversityName,
		universityLogoUrl: applicationUniversityLogoUrl,
		applicationTargetId,
	})
	const initialEditSnapshotRef = useRef(null)
	const lastEssayAutoDescriptionRef = useRef('')
	const lastLorAutoDescriptionRef = useRef('')
	const isSubmittingRef = useRef(false)
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const dropdownRef = useRef(null)
	const [localActiveGrade, setLocalActiveGrade] = useState(activeGrade)
	const isMobile = useIsMobile()

	const allStudents = studentsStore.students || []
	const grade10 = allStudents.filter(s => Number(s.grade) === 10)
	const grade11 = allStudents.filter(s => Number(s.grade) === 11)
	const grade12 = allStudents.filter(s => Number(s.grade) === 12)

	const gradeLists = [
		{ id: 10, label: 'Grade 10', count: grade10.length },
		{ id: 11, label: 'Grade 11', count: grade11.length },
		{ id: 12, label: 'Grade 12', count: grade12.length },
	]

	const selectedStudentsFromStore = studentsStore.selectedStudents || []

	const studentsToUse =
		selectedStudents.length > 0 ? selectedStudents : selectedStudentsFromStore
	const steps = useMemo(
		() => [
			{ id: 'students', label: 'Students', icon: StudentsIcon },
			{ id: 'assignmentType', label: 'Assignment Type', icon: TypeIcon },
			{ id: 'timeline', label: 'Timeline', icon: TimeIcon },
			{ id: 'description', label: 'Description', icon: DescrIcon },
			{ id: 'review', label: 'Review', icon: ReviewIcon },
		],
		[],
	)

	const [stepIndex, setStepIndex] = useState(0)

	const [activityType, setActivityType] = useState(initialAssignmentType || '')

	const [activities] = useState(MOCK_ACTIVITIES)
	const [selectedActivityIds, setSelectedActivityIds] = useState([])

	const [selectedStudentsLocal, setSelectedStudentsLocal] = useState([])

	const [isTimelineValid, setIsTimelineValid] = useState(false)
	const [timelineData, setTimelineData] = useState({
		openDate: '',
		openTime: '',
		closeDate: '',
		closeTime: '',
		openDt: null,
		closeDt: null,
	})

	const [preview, setPreview] = useState({
		deadline: null,
		flags: {
			notifyEverySubmission: false,
			notifyWhenHalfComplete: false,
			remind1DayBefore: false,
		},
	})

	const [showToast, setShowToast] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [activityText, setActivityText] = useState('')
	const [isEditing, setIsEditing] = useState(false)
	const [isSaved, setIsSaved] = useState(false)
	const [askAiText, setAskAiText] = useState('')
	const [description, setDescription] = useState('')
	const [customMessage, setCustomMessage] = useState('')
	const [documentTargetsPayload, setDocumentTargetsPayload] = useState([])
	const [essayPrompt, setEssayPrompt] = useState('')
	const [essayWordLimit, setEssayWordLimit] = useState('')
	const [essayMode, setEssayMode] = useState(
		initialEssayMode === 'common_app' ? 'common_app' : 'university',
	)
	const [lorRecommenderName, setLorRecommenderName] = useState('')
	const [lorProfessorDesignation, setLorProfessorDesignation] = useState('')
	const parsedEssayWordLimit = useMemo(
		() => parseEssayWordLimit(essayWordLimit),
		[essayWordLimit],
	)

	const onDocumentTargetsChange = useCallback(targets => {
		setDocumentTargetsPayload(Array.isArray(targets) ? targets : [])
	}, [])

	useEffect(() => {
		if (!isOpen) return

		isSubmittingRef.current = false
		setIsSubmitting(false)

		if (editMode && assignment) {
			const assignmentData =
				assignment?.assignmentData || assignment?.data || {}
			const students = assignment?.students || []

			const rawType = assignment?.type || assignmentData?.type || ''
			const taskType = rawType === 'LOR' ? 'LoR' : rawType
			const taskDescription = assignmentData?.description || ''
			const closeDate = assignmentData?.end_date || null
			const openDate =
				assignmentData?.start_date || assignment?.createdAt || null
			const customMsg = assignmentData?.custom_message || ''

			setActivityType(taskType)
			setDescription(taskDescription)
			setActivityText(taskDescription)
			setCustomMessage(customMsg)

			const parseDate = dateStr => {
				if (!dateStr) return null
				const dt = new Date(dateStr)
				return isNaN(dt.getTime()) ? null : dt
			}

			const openDt = parseDate(openDate)
			const closeDt = parseDate(closeDate)

			const formatDateISO = dt => {
				if (!dt) return ''
				const year = dt.getFullYear()
				const month = String(dt.getMonth() + 1).padStart(2, '0')
				const day = String(dt.getDate()).padStart(2, '0')
				return `${year}-${month}-${day}`
			}

			const formatTime = dt => {
				if (!dt) return ''
				const hours = String(dt.getHours()).padStart(2, '0')
				const minutes = String(dt.getMinutes()).padStart(2, '0')
				return `${hours}:${minutes}`
			}

			const timelineDataToSet = {
				openDate: openDt ? formatDateISO(openDt) : '',
				openTime: openDt ? formatTime(openDt) : '',
				closeDate: closeDt ? formatDateISO(closeDt) : '',
				closeTime: closeDt ? formatTime(closeDt) : '',
				openDt: openDt,
				closeDt: closeDt,
			}

			setTimelineData(timelineDataToSet)

			if (closeDt) {
				setIsTimelineValid(true)
			}

			if (Array.isArray(students) && students.length > 0) {
				setSelectedStudentsLocal(students)
			}

			const normalizeText = value => String(value || '').trim()
			initialEditSnapshotRef.current = {
				openIso: openDt ? openDt.toISOString() : null,
				closeIso: closeDt ? closeDt.toISOString() : null,
				description: normalizeText(taskDescription),
				customMessage: normalizeText(customMsg),
			}

			setCompletedSteps(new Set(['students', 'assignmentType']))

			const timelineStepIndex = steps.findIndex(s => s.id === 'timeline')
			if (timelineStepIndex !== -1) {
				setStepIndex(timelineStepIndex)
			}
		} else {
			initialEditSnapshotRef.current = null
			const assignmentTypeStepIndex = steps.findIndex(
				step => step.id === 'assignmentType',
			)
			const requestedStepIndex = initialStepId
				? steps.findIndex(step => step.id === initialStepId)
				: -1
			if (requestedStepIndex !== -1) {
				setStepIndex(requestedStepIndex)
			} else if (
				(tepeModal === 'applications' || lockRecipients) &&
				assignmentTypeStepIndex !== -1
			) {
				setStepIndex(assignmentTypeStepIndex)
			} else {
				setStepIndex(0)
			}
			const normType =
				initialAssignmentType === 'LOR' ? 'LoR' : initialAssignmentType || ''
			setActivityType(normType)
			setSelectedActivityIds([])
			setPreview({
				deadline: null,
				flags: {
					notifyEverySubmission: false,
					notifyWhenHalfComplete: false,
					remind1DayBefore: false,
				},
			})
			setActivityText('')
			setIsEditing(false)
			setIsSaved(false)
			setAskAiText('')
			setDescription('')
			setCustomMessage('')
			setEssayPrompt('')
			setEssayWordLimit('')
			setEssayMode('university')
			setSelectedEssayUniversityTargetId(applicationTargetId)
			setSelectedEssayUniversityMeta({
				universityId: applicationUniversityId,
				universityName: applicationUniversityName,
				universityLogoUrl: applicationUniversityLogoUrl,
				applicationTargetId,
			})
			setLorRecommenderName('')
			setLorProfessorDesignation('')
			setTimelineData({
				openDate: '',
				openTime: '',
				closeDate: '',
				closeTime: '',
				openDt: null,
				closeDt: null,
			})
			setIsTimelineValid(false)
			if (requestedStepIndex !== -1) {
				const completed = new Set()
				for (let i = 0; i < requestedStepIndex; i += 1) {
					completed.add(steps[i].id)
				}
				setCompletedSteps(completed)
			} else {
				setCompletedSteps(new Set())
			}
			setDocumentTargetsPayload([])
		}
	}, [
		isOpen,
		editMode,
		assignment,
		steps,
		initialAssignmentType,
		initialStepId,
		tepeModal,
		applicationTargetId,
		applicationUniversityId,
		applicationUniversityLogoUrl,
		applicationUniversityName,
		lockRecipients,
	])

	useEffect(() => {
		if (!isOpen || tepeModal !== 'applications') return
		if (!['Essay', 'Document', 'LoR'].includes(activityType)) return
		const hasPresetTarget = Boolean(applicationTargetId)
		if (hasPresetTarget) return
		const studentId = String(
			selectedStudentsLocal?.[0]?.id ||
				selectedStudentsLocal?.[0]?.student_id ||
				selectedStudentsLocal?.[0]?.studentId ||
				'',
		).trim()
		if (!studentId) return
		let alive = true
		const run = async () => {
			try {
				setIsEssayUniversityLoading(true)
				const data = await fetchStudentApplications(studentId)
				if (!alive) return
				const options = (Array.isArray(data?.applications)
					? data.applications
					: []
				).map(app => ({
					applicationTargetId: String(
						app?.applicationTargetId || app?.application_target_id || app?.id || '',
					).trim(),
					universityId: String(app?.universityId || app?.university_id || '').trim(),
					universityName: String(
						app?.university || app?.universityName || 'University',
					).trim(),
					universityLogoUrl: String(
						app?.universityLogoUrl || app?.logo_s3_link || app?.logoUrl || '',
					).trim(),
				}))
				setEssayUniversityOptions(options.filter(x => x.applicationTargetId))
				if (!selectedEssayUniversityTargetId && options[0]?.applicationTargetId) {
					setSelectedEssayUniversityTargetId(options[0].applicationTargetId)
					setSelectedEssayUniversityMeta(options[0])
				}
			} catch (e) {
				if (!alive) return
				setEssayUniversityOptions([])
			} finally {
				if (alive) setIsEssayUniversityLoading(false)
			}
		}
		run()
		return () => {
			alive = false
		}
	}, [
		isOpen,
		tepeModal,
		activityType,
		applicationTargetId,
		selectedStudentsLocal,
		selectedEssayUniversityTargetId,
	])

	useEffect(() => {
		if (!essayUniversityOptions.length || !selectedEssayUniversityTargetId) return
		const hit = essayUniversityOptions.find(
			x => x.applicationTargetId === selectedEssayUniversityTargetId,
		)
		if (hit) setSelectedEssayUniversityMeta(hit)
	}, [essayUniversityOptions, selectedEssayUniversityTargetId])

	useEffect(() => {
		if (!isOpen || tepeModal !== 'applications') return
		if (activityType !== 'Document') return
		if (!documentTargetsPayload.length) return
		const lines = documentTargetsPayload.map(
			t => `• ${t.title || t.requirementId || 'Document'}`,
		)
		const auto = `Please complete the following:\n\n${lines.join('\n')}`
		setDescription(auto)
		setActivityText(auto)
	}, [documentTargetsPayload, isOpen, tepeModal, activityType])

	useEffect(() => {
		if (!isOpen || tepeModal !== 'applications' || activityType !== 'Essay') {
			lastEssayAutoDescriptionRef.current = ''
			return
		}

		const prompt = String(essayPrompt || '').trim()
		const limitNum = parsedEssayWordLimit
		if (!prompt || !limitNum) return

		const auto = `Please write an essay "${prompt}", with a maximum word count of ${limitNum} words.`

		let didApplyAuto = false
		setDescription(prev => {
			const trimmed = String(prev || '').trim()
			if (
				!trimmed ||
				trimmed === lastEssayAutoDescriptionRef.current ||
				isEssayAutoDescription(trimmed)
			) {
				didApplyAuto = true
				return auto
			}
			return prev
		})

		setActivityText(prev => {
			const trimmed = String(prev || '').trim()
			if (
				!trimmed ||
				trimmed === lastEssayAutoDescriptionRef.current ||
				isEssayAutoDescription(trimmed)
			)
				return auto
			return prev
		})

		if (didApplyAuto) {
			lastEssayAutoDescriptionRef.current = auto
		}
	}, [
		isOpen,
		tepeModal,
		activityType,
		essayPrompt,
		parsedEssayWordLimit,
		essayMode,
	])

	useEffect(() => {
		if (!isOpen || tepeModal !== 'applications' || activityType !== 'LoR') {
			lastLorAutoDescriptionRef.current = ''
			return
		}
		const name = String(lorRecommenderName || '').trim()
		const des = String(lorProfessorDesignation || '').trim()
		if (!name || !des) return
		const auto = `Your Letter of Recommendation will be prepared by ${name}, ${des}.

To help them write a strong and personalized recommendation, please complete your Brag Sheet and ensure your profile is up to date.

If any additional information is required, your consultant will reach out to you.`

		setDescription(prev => {
			const trimmed = String(prev || '').trim()
			if (!trimmed || trimmed === lastLorAutoDescriptionRef.current) return auto
			return prev
		})
		setActivityText(prev => {
			const trimmed = String(prev || '').trim()
			if (!trimmed || trimmed === lastLorAutoDescriptionRef.current) return auto
			return prev
		})

		lastLorAutoDescriptionRef.current = auto
	}, [
		isOpen,
		tepeModal,
		activityType,
		lorRecommenderName,
		lorProfessorDesignation,
	])

	useEffect(() => {
		if (!isOpen || editMode) return
		const incoming = Array.isArray(studentsToUse) ? studentsToUse : []
		setSelectedStudentsLocal(incoming)
	}, [isOpen, studentsToUse, editMode])

	// When modal opens: load students, optionally clear selection. Saved lists are temporarily disabled.
	useEffect(() => {
		if (!isOpen) return
		if (clearSelectionOnOpen) {
			studentsStore.clearSelection()
		}
		let cancelled = false
		const load = async () => {
			try {
				await ensureStudentsLoaded()
				// if (!cancelled) studentsStore.loadStudentLists()
			} catch (e) {
				if (!cancelled)
					console.error('Failed to load students for Create Assignment', e)
			}
		}
		load()
		return () => {
			cancelled = true
		}
	}, [isOpen, clearSelectionOnOpen])

	useEffect(() => {
		if (!isOpen) {
			return
		}
		const initialGrade = activeGrade || 'all'
		setLocalActiveGrade(initialGrade)
	}, [isOpen, activeGrade])

	const close = useCallback(() => {
		onClose?.()
	}, [onClose])

	useEffect(() => {
		if (!isOpen) return

		const onKeyDown = e => {
			if (e.key === 'Escape') {
				if (isDropdownOpen) {
					setIsDropdownOpen(false)
				} else {
					close()
				}
			}
		}

		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [isOpen, close, isDropdownOpen])

	useEffect(() => {
		if (!isDropdownOpen) return

		const handleClickOutside = e => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setIsDropdownOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isDropdownOpen])

	const handleSelectList = useCallback(
		gradeOrListId => {
			if (gradeOrListId === null) {
				setLocalActiveGrade('all')
				if (onSetGrade) onSetGrade('all')
				studentsStore.setGrade('all')
			} else if (
				typeof gradeOrListId === 'string' &&
				isNaN(Number(gradeOrListId))
			) {
				const listId = gradeOrListId
				setLocalActiveGrade('list:' + listId)
				if (onSetGrade) onSetGrade('list:' + listId)
				studentsStore.setGrade('list:' + listId)
			} else {
				const grade = Number(gradeOrListId)
				setLocalActiveGrade(grade)
				if (onSetGrade) onSetGrade(grade)
				studentsStore.setGrade(grade)
			}
			setIsDropdownOpen(false)
		},
		[onSetGrade],
	)

	const activeStep = steps[stepIndex]

	const selectActivityType = useCallback(id => setActivityType(id), [])

	const toggleActivity = useCallback(id => {
		setSelectedActivityIds(prev =>
			prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
		)
	}, [])

	const setPreviewField = useCallback((key, value) => {
		setPreview(prev => ({ ...prev, [key]: value }))
	}, [])

	const togglePreviewFlag = useCallback(flagKey => {
		setPreview(prev => ({
			...prev,
			flags: { ...prev.flags, [flagKey]: !prev.flags[flagKey] },
		}))
	}, [])

	const setTimelineDataHandler = useCallback(data => {
		setTimelineData(data)
	}, [])

	const selectedActivityTitle = useMemo(() => {
		const customTitle = String(initialTaskTitle || '').trim()
		if (customTitle) return customTitle
		if (!activityType) return 'Form Completion'
		if (activityType === 'Document') return 'Documents'
		if (activityType === 'LoR') return 'Letter of Recommendation'
		const type = ACTIVITY_TYPES.find(t => t.id === activityType)
		return type?.title || 'Form Completion'
	}, [activityType, initialTaskTitle])

	// Apply default description when assignment type is selected.

	// Skip this in edit mode to preserve existing description.
	useEffect(() => {
		if (editMode) return
		if (!activityType) return
		if (tepeModal === 'applications' && activityType === 'Document') return
		const defaultText = DEFAULT_DESCRIPTIONS_BY_TYPE[activityType]
		if (!defaultText) return

		const allDefaults = Object.values(DEFAULT_DESCRIPTIONS_BY_TYPE)

		setDescription(prev => {
			const trimmed = (prev || '').trim()
			if (!trimmed) return defaultText
			if (allDefaults.includes(trimmed)) return defaultText
			return prev
		})

		setActivityText(prev => {
			const trimmed = (prev || '').trim()
			if (!trimmed) return defaultText
			if (allDefaults.includes(trimmed)) return defaultText
			return prev
		})
	}, [activityType, editMode, tepeModal])

	const startEdit = useCallback(() => {
		setIsEditing(true)
		setIsSaved(false)
	}, [])

	const acceptEdit = useCallback(() => {
		setIsEditing(false)
		setIsSaved(true)
	}, [])

	const onAskAi = useCallback(async () => {}, [])

	const isEditDirty = useMemo(() => {
		if (!editMode) return false
		const initial = initialEditSnapshotRef.current
		if (!initial) return false

		const normalizeText = value => String(value || '').trim()
		const currentOpenIso = timelineData?.openDt
			? new Date(timelineData.openDt).toISOString()
			: null
		const currentCloseIso = timelineData?.closeDt
			? new Date(timelineData.closeDt).toISOString()
			: null

		return (
			initial.openIso !== currentOpenIso ||
			initial.closeIso !== currentCloseIso ||
			initial.description !== normalizeText(description) ||
			initial.customMessage !== normalizeText(customMessage)
		)
	}, [
		editMode,
		timelineData?.openDt,
		timelineData?.closeDt,
		description,
		customMessage,
	])

	const canGoNext = useMemo(() => {
		if (activeStep.id === 'students') {
			return (selectedStudentsLocal?.length || 0) > 0
		}
		if (activeStep.id === 'assignmentType') {
			if (tepeModal === 'applications' && activityType === 'Document') {
				const targetOk = Boolean(
					selectedEssayUniversityMeta?.applicationTargetId ||
						applicationTargetId,
				)
				return (
					Boolean(activityType) &&
					(documentTargetsPayload?.length || 0) > 0 &&
					targetOk
				)
			}
			if (tepeModal === 'applications' && activityType === 'Essay') {
				const promptOk = Boolean(String(essayPrompt || '').trim())
				const limitOk = Boolean(parsedEssayWordLimit)
				const targetOk =
					essayMode === 'common_app'
						? true
						: Boolean(
								selectedEssayUniversityMeta?.applicationTargetId || applicationTargetId,
						  )
				return Boolean(activityType) && promptOk && limitOk && targetOk
			}
			if (tepeModal === 'applications' && activityType === 'LoR') {
				const targetOk = Boolean(
					selectedEssayUniversityMeta?.applicationTargetId ||
						applicationTargetId,
				)
				return (
					Boolean(activityType) &&
					Boolean(String(lorRecommenderName || '').trim()) &&
					Boolean(String(lorProfessorDesignation || '').trim()) &&
					targetOk
				)
			}
			return Boolean(activityType)
		}
		if (activeStep.id === 'timeline') {
			// In edit mode allow continue when both dates are filled (user can fix range on next step or on submit)
			if (editMode && timelineData?.openDt && timelineData?.closeDt) {
				return true
			}
			return isTimelineValid
		}
		if (activeStep.id === 'description') {
			return Boolean(description && description.trim().length > 0)
		}
		return true
	}, [
		activeStep.id,
		activityType,
		selectedStudentsLocal,
		isTimelineValid,
		description,
		editMode,
		timelineData?.openDt,
		timelineData?.closeDt,
		tepeModal,
		documentTargetsPayload,
		essayPrompt,
		parsedEssayWordLimit,
		essayMode,
		selectedEssayUniversityMeta?.applicationTargetId,
		lorRecommenderName,
		lorProfessorDesignation,
		applicationTargetId,
	])

	const isPrimaryButtonDisabled = useMemo(() => {
		if (isSubmitting) return true
		const isReviewStep = activeStep.id === 'review'
		if (isReviewStep && editMode) return !isEditDirty
		if (!isReviewStep) return !canGoNext
		return false
	}, [activeStep.id, editMode, isEditDirty, canGoNext, isSubmitting])

	const [completedSteps, setCompletedSteps] = useState(new Set())

	const markStepCompleted = useCallback(stepId => {
		setCompletedSteps(prev => new Set([...prev, stepId]))
	}, [])

	const isStepAccessible = useCallback(
		targetIndex => {
			const targetStep = steps[targetIndex]
			if (
				(tepeModal === 'applications' || lockRecipients) &&
				targetStep?.id === 'students'
			) {
				return false
			}
			if (editMode) {
				if (
					targetStep &&
					(targetStep.id === 'students' || targetStep.id === 'assignmentType')
				) {
					return false
				}
			}
			if (targetIndex === stepIndex) return true
			if (targetIndex < stepIndex) return true
			if (targetIndex === stepIndex + 1) {
				return canGoNext
			}
			return false
		},
		[stepIndex, canGoNext, editMode, steps, tepeModal, lockRecipients],
	)

	const isStepLocked = useCallback(
		stepId => {
			if ((tepeModal === 'applications' || lockRecipients) && stepId === 'students') {
				return true
			}
			return editMode && (stepId === 'students' || stepId === 'assignmentType')
		},
		[editMode, tepeModal, lockRecipients],
	)

	const goToStep = useCallback(
		targetIndex => {
			if (targetIndex < 0 || targetIndex >= steps.length) return
			const stepId = steps[targetIndex]?.id
			if (!isStepAccessible(targetIndex) || (stepId && isStepLocked(stepId)))
				return
			if (targetIndex > stepIndex && canGoNext) {
				markStepCompleted(activeStep.id)
			}
			setStepIndex(targetIndex)
		},
		[
			stepIndex,
			steps,
			isStepAccessible,
			isStepLocked,
			canGoNext,
			activeStep.id,
			markStepCompleted,
		],
	)

	const goNext = useCallback(() => {
		if (!canGoNext) return
		markStepCompleted(activeStep.id)
		setStepIndex(prev => Math.min(prev + 1, steps.length - 1))
	}, [steps.length, canGoNext, activeStep.id, markStepCompleted])

	const goBack = useCallback(() => {
		if (tepeModal === 'applications' || lockRecipients) {
			const assignmentTypeStepIndex = steps.findIndex(
				step => step.id === 'assignmentType',
			)
			if (stepIndex <= assignmentTypeStepIndex) return
		}
		if (editMode) {
			const timelineStepIndex = steps.findIndex(s => s.id === 'timeline')
			if (stepIndex === timelineStepIndex) {
				return
			}
		}
		setStepIndex(prev => Math.max(prev - 1, 0))
	}, [editMode, stepIndex, steps, tepeModal, lockRecipients])

	const onSendNow = useCallback(async () => {
		if (isSubmittingRef.current) return

		try {
			if (editMode && !isEditDirty) {
				return
			}

			const studentIds = selectedStudentsLocal
				.map(s => {
					return (
						s.id ||
						s.student_id ||
						s.studentId ||
						(typeof s === 'string' ? s : null)
					)
				})
				.filter(Boolean)

			if (studentIds.length === 0) {
				showErrorSafe('No students selected. Please select at least one student.')
				return
			}

			const openDate = timelineData?.openDt
				? new Date(timelineData.openDt).toISOString()
				: null
			const closeDate = timelineData?.closeDt
				? new Date(timelineData.closeDt).toISOString()
				: null

			if (!closeDate) {
				showErrorSafe('Please set a closing date for the assignment.')
				return
			}

			const closeDt = timelineData?.closeDt
				? new Date(timelineData.closeDt)
				: null
			const openDt = timelineData?.openDt ? new Date(timelineData.openDt) : null
			if (closeDt && openDt && closeDt <= openDt) {
				showErrorSafe(
					'Closing date must be after the opening date. Please correct the timeline.',
				)
				return
			}

			isSubmittingRef.current = true
			setIsSubmitting(true)

			const taskData = {
				task_title: selectedActivityTitle || 'New Assignment',
				task_description: description || activityText || '',
				task_type: activityType || 'General',
				due_date: closeDate,
				open_date: openDate,
				student_ids: studentIds,
				custom_message: customMessage || null,
			}

			if (tepeModal === 'applications' && activityType === 'Essay') {
				const prompt = String(essayPrompt || '').trim()
				const limitNum = parsedEssayWordLimit
				const isCommonAppEssay = essayMode === 'common_app'
				const essayUniversityId =
					selectedEssayUniversityMeta?.universityId || applicationUniversityId
				const essayUniversityName =
					selectedEssayUniversityMeta?.universityName || applicationUniversityName
				const essayUniversityLogo =
					selectedEssayUniversityMeta?.universityLogoUrl ||
					applicationUniversityLogoUrl
				const essayApplicationTargetId =
					selectedEssayUniversityMeta?.applicationTargetId || applicationTargetId
				taskData.task_title = prompt
					? `Create essay: ${prompt}`
					: 'Create essay'
				const essayPayload = {}
				if (!isCommonAppEssay && essayUniversityId) {
					essayPayload.university_id = essayUniversityId
				}
				if (!isCommonAppEssay && essayUniversityName) {
					essayPayload.university_name = essayUniversityName
				}
				if (!isCommonAppEssay && essayUniversityLogo) {
					essayPayload.logo_s3_link = essayUniversityLogo
				}
				if (essayApplicationTargetId) {
					essayPayload.application_target_id = essayApplicationTargetId
				}
				essayPayload.essay_scope = isCommonAppEssay ? 'common_app' : 'individual'
				essayPayload.is_common_app = isCommonAppEssay
				if (limitNum) {
					essayPayload.essay_title = prompt
					essayPayload.word_limit = limitNum
					taskData.task_payload = essayPayload
				} else if (prompt) {
					essayPayload.essay_title = prompt
					taskData.task_payload = essayPayload
				} else if (isCommonAppEssay || essayUniversityId || essayApplicationTargetId) {
					taskData.task_payload = essayPayload
				}
			}

			if (
				tepeModal === 'applications' &&
				activityType === 'Document' &&
				documentTargetsPayload.length > 0
			) {
				taskData.document_targets = documentTargetsPayload.map(
					({ requirementId, action, title }) => ({
						requirementId,
						action: action || 'upload_new',
						...(title ? { title } : {}),
					}),
				)
				const documentApplicationTargetId =
					selectedEssayUniversityMeta?.applicationTargetId || applicationTargetId
				if (documentApplicationTargetId) {
					taskData.task_payload = {
						application_target_id: documentApplicationTargetId,
					}
				}
			}

			if (tepeModal === 'applications' && activityType === 'LoR') {
				const lorTargetId =
					selectedEssayUniversityMeta?.applicationTargetId ||
					applicationTargetId
				if (!lorTargetId) {
					showErrorSafe('Missing application target for this LoR assignment.')
					return
				}
				const name = String(lorRecommenderName || '').trim()
				const des = String(lorProfessorDesignation || '').trim()
				const uniLabel =
					String(
						selectedEssayUniversityMeta?.universityName ||
							applicationUniversityName ||
							'',
					).trim() || 'University'
				taskData.task_title = `LoR — ${name} (${des}) — ${uniLabel}`
				taskData.task_type = 'LoR'
				const lorPayload = {
					application_target_id:
						selectedEssayUniversityMeta?.applicationTargetId || applicationTargetId,
					recommender_name: name,
					professor_designation: des,
				}
				if (selectedEssayUniversityMeta?.universityId || applicationUniversityId) {
					lorPayload.university_id =
						selectedEssayUniversityMeta?.universityId || applicationUniversityId
				}
				if (selectedEssayUniversityMeta?.universityName || applicationUniversityName) {
					lorPayload.university_name =
						selectedEssayUniversityMeta?.universityName || applicationUniversityName
				}
				if (
					selectedEssayUniversityMeta?.universityLogoUrl ||
					applicationUniversityLogoUrl
				) {
					lorPayload.logo_s3_link =
						selectedEssayUniversityMeta?.universityLogoUrl ||
						applicationUniversityLogoUrl
				}
				taskData.task_payload = lorPayload
			}

			let result
			if (editMode && assignment) {
				const taskId =
					assignment?.id ||
					assignment?.task_id ||
					assignment?.assignmentData?.task_id ||
					assignment?.assignmentData?.id
				if (!taskId) {
					showErrorSafe('Cannot update assignment: missing task ID.')
					return
				}

				const patch = {
					task_description: taskData.task_description || '',
					custom_message: taskData.custom_message || '',
					open_date: taskData.open_date,
					due_date: taskData.due_date,
				}

				result = await updateTask(taskId, patch)
				console.log('✅ Task updated successfully:', result)
			} else {
				result = await createTask(taskData)
				console.log('✅ Task created successfully:', result)
			}

			if (onSuccess) {
				// Add small delay to ensure backend has processed the request
				await new Promise(resolve => setTimeout(resolve, 500))
				console.log('🔄 Refreshing assignments list...')
				await onSuccess()
			}

			close()
			setShowToast(true)
		} catch (error) {
			console.error(`Failed to ${editMode ? 'update' : 'create'} task:`, error)
			showErrorSafe(
				error.response?.data?.error ||
					error.message ||
					`Failed to ${editMode ? 'update' : 'create'} assignment. Please try again.`,
			)
			isSubmittingRef.current = false
			setIsSubmitting(false)
		}
	}, [
		close,
		activityType,
		selectedStudentsLocal,
		timelineData,
		selectedActivityTitle,
		description,
		activityText,
		customMessage,
		essayPrompt,
		parsedEssayWordLimit,
		essayMode,
		onSuccess,
		editMode,
		assignment,
		isEditDirty,
		documentTargetsPayload,
		tepeModal,
		applicationUniversityId,
		applicationUniversityName,
		applicationUniversityLogoUrl,
		applicationTargetId,
		selectedEssayUniversityMeta?.applicationTargetId,
		selectedEssayUniversityMeta?.universityId,
		selectedEssayUniversityMeta?.universityLogoUrl,
		selectedEssayUniversityMeta?.universityName,
		lorRecommenderName,
		lorProfessorDesignation,
		showErrorSafe,
	])

	const closeToast = useCallback(() => {
		setShowToast(false)
	}, [])

	const toastMessage = useMemo(() => {
		return editMode
			? 'Assignment updated successfully!'
			: 'Assignment sent successfully!'
	}, [editMode])

	const listLabel = useMemo(() => {
		const count = selectedStudentsLocal.length
		const formatCount = n => String(n).padStart(2, '0')

		if (!count) {
			return 'All Students (00)'
		}

		if (
			typeof localActiveGrade === 'number' ||
			(typeof localActiveGrade === 'string' && !isNaN(Number(localActiveGrade)))
		) {
			const grade = Number(localActiveGrade)
			if (grade === 10 || grade === 11 || grade === 12) {
				return `Grade ${grade} (${formatCount(count)})`
			}
		}

		if (
			typeof localActiveGrade === 'string' &&
			localActiveGrade.startsWith('list:')
		) {
			const listId = localActiveGrade.substring('list:'.length)
			const savedListsFromStore = studentsStore.savedLists || []
			const list = savedListsFromStore.find(l => l.id === listId)

			if (list && Array.isArray(list.studentIds) && list.studentIds.length) {
				const listIds = new Set(list.studentIds.map(id => String(id).trim()))
				const selectedIds = selectedStudentsLocal.map(s => String(s.id).trim())
				const allSelectedFromList =
					selectedIds.length === listIds.size &&
					selectedIds.every(id => listIds.has(id))

				if (allSelectedFromList) {
					return `${list.name} (${formatCount(count)})`
				}
			}
		}

		return `All Students (${formatCount(count)})`
	}, [selectedStudentsLocal, localActiveGrade])

	const isFirstStep = useMemo(() => {
		if (tepeModal === 'applications') {
			const assignmentTypeStepIndex = steps.findIndex(
				step => step.id === 'assignmentType',
			)
			return stepIndex === assignmentTypeStepIndex
		}
		if (editMode) {
			const timelineStepIndex = steps.findIndex(s => s.id === 'timeline')
			return stepIndex === timelineStepIndex
		}
		return activeStep.id === 'students'
	}, [editMode, activeStep.id, stepIndex, steps, tepeModal])

	const isLastStep = useMemo(() => activeStep.id === 'review', [activeStep.id])

	const handleBackClick = useCallback(() => {
		if (isSubmittingRef.current || isFirstStep) return
		goBack()
	}, [isFirstStep, goBack])

	const handleNextClick = useCallback(() => {
		if (isSubmittingRef.current) return
		if (isLastStep) {
			onSendNow()
			return
		}
		if (activeStep.id === 'description' && description) {
			setActivityText(description)
		}
		if (!canGoNext) return
		goNext()
	}, [
		isLastStep,
		canGoNext,
		goNext,
		onSendNow,
		activeStep.id,
		description,
		setActivityText,
	])

	const totalSteps = steps.length
	const currentStepNumber = stepIndex + 1

	const modalTitle = useMemo(() => {
		if (editMode) return 'Edit Assignment'
		if (tepeModal === 'applications' && activityType === 'Essay')
			return 'Create Essay'
		if (tepeModal === 'applications' && activityType === 'Document')
			return 'Create Documents'
		if (tepeModal === 'applications' && activityType === 'LoR')
			return 'Create Letter of Recommendation'
		return 'Create Assignment'
	}, [editMode, tepeModal, activityType])

	const buttonText = useMemo(() => {
		if (isLastStep && isSubmitting) {
			return editMode ? 'Updating...' : 'Sending...'
		}
		if (isLastStep) {
			return editMode ? 'Update Assignment' : 'Send Assignment'
		}
		return 'Continue'
	}, [isLastStep, editMode, isSubmitting])

	return {
		steps,
		activeStep,
		stepIndex,
		totalSteps,
		currentStepNumber,

		activityType,
		selectActivityType,

		activities,
		selectedActivityIds,
		toggleActivity,

		selectedStudentsLocal,
		setSelectedStudentsLocal,
		setIsTimelineValid,
		timelineData,
		setTimelineDataHandler,

		preview,
		setPreviewField,
		togglePreviewFlag,
		selectedActivityTitle,
		activityText,
		setActivityText,
		isEditing,
		startEdit,
		acceptEdit,
		isSaved,
		askAiText,
		setAskAiText,
		onAskAi,

		description,
		setDescription,
		customMessage,
		setCustomMessage,

		essayPrompt,
		setEssayPrompt,
		essayWordLimit,
		setEssayWordLimit,
		essayMode,
		setEssayMode,

		lorRecommenderName,
		setLorRecommenderName,
		lorProfessorDesignation,
		setLorProfessorDesignation,

		isFirstStep,
		isLastStep,
		canGoNext,
		isPrimaryButtonDisabled,
		isSubmitting,
		handleBackClick,
		handleNextClick,
		goToStep,
		isStepAccessible,
		isStepLocked,
		completedSteps,

		close,

		showToast,
		closeToast,
		toastMessage,

		listLabel,
		modalTitle,
		buttonText,

		gradeLists,
		localActiveGrade,
		isDropdownOpen,
		setIsDropdownOpen,
		dropdownRef,
		handleSelectList,
		savedLists: studentsStore.savedLists || [],
		isMobile,

		documentTargetsPayload,
		onDocumentTargetsChange,
		essayUniversityOptions,
		selectedEssayUniversityTargetId,
		setSelectedEssayUniversityTargetId,
		selectedEssayUniversityMeta,
		isEssayUniversityLoading,
	}
}
